import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import { UserData } from '../../../services/userService';
import CustomDropdown from '../../../components/CustomDropdown/CustomDropdown';
import { PORTFOLIO_SORT_OPTIONS, PortfolioSort } from '../../../stores/customerHealthStore';
import { PortfolioRow as PortfolioRowType, PortfolioSummary } from '../types';
import { healthStatusMeta, budgetToneFromBacklogPct } from '../meta';
import { money, pctOf, userDisplayName } from '../helpers';
import { Dot, Badge } from './primitives';

/** The whole-portfolio KPI band (reflects every tracked parent, independent of the current page). */
export function SummaryBand({ summary }: { summary: PortfolioSummary }) {
  const total = summary.trackedCount || 1;
  const { healthyCount, watchCount, criticalCount } = summary;
  return (
    <div className="ch-kpis">
      <div className="ch-card">
        <div className="ch-kpi-label">Portfolio Health</div>
        <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', marginBottom: 12, background: '#f0f0f0' }}>
          {healthyCount > 0 && <span style={{ width: (healthyCount / total * 100) + '%', background: '#51cf66', height: '100%' }} />}
          {watchCount > 0 && <span style={{ width: (watchCount / total * 100) + '%', background: '#f5c518', height: '100%' }} />}
          {criticalCount > 0 && <span style={{ width: (criticalCount / total * 100) + '%', background: '#e11900', height: '100%' }} />}
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: '#51cf66' }} /><span style={{ fontSize: 13, color: '#333' }}><b style={{ fontWeight: 700 }}>{healthyCount}</b> Healthy</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: '#f5c518' }} /><span style={{ fontSize: 13, color: '#333' }}><b style={{ fontWeight: 700 }}>{watchCount}</b> Watch</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: '#e11900' }} /><span style={{ fontSize: 13, color: '#333' }}><b style={{ fontWeight: 700 }}>{criticalCount}</b> Critical</span></div>
        </div>
      </div>
      <div className="ch-card"><div className="ch-kpi-label sm">Total Backlog</div><div className="ch-kpi-num">{money(summary.totalBacklog)}</div><div className="ch-kpi-sub">Across tracked parents</div></div>
      <div className="ch-card"><div className="ch-kpi-label sm">Revenue YTD</div><div className="ch-kpi-num">{money(summary.totalRevenueYtd)}</div><div className="ch-kpi-sub">{pctOf(summary.totalRevenueYtd, summary.totalAnnualBudget)}% of {money(summary.totalAnnualBudget)} budget</div></div>
      <div className="ch-card"><div className="ch-kpi-label sm">Open Pipeline</div><div className="ch-kpi-num">{money(summary.openPipelineValue)}</div><div className="ch-kpi-sub">{summary.openPipelineCount} open deals</div></div>
      <div className="ch-card"><div className="ch-kpi-label sm">Open Action Items</div><div className="ch-kpi-num">{summary.openActions}</div><div className="ch-kpi-sub" style={{ color: '#e11900', fontWeight: 600 }}>{summary.breachedActions > 0 ? summary.breachedActions + ' TAT breached' : 'None breached'}</div></div>
    </div>
  );
}

/** One ranked ultimate-parent row; clicking opens its detail page. */
export function PortfolioRow({ row, byId, onOpen }: { row: PortfolioRowType; byId: Map<string, UserData>; onOpen: () => void }) {
  const budgetPct = pctOf(row.backlogAmount, row.annualBudget);
  return (
    <div className="ch-parent-row" onClick={onOpen} style={{ borderLeft: '4px solid ' + healthStatusMeta(row.trackStatus).c, cursor: 'pointer' }}>
      <div style={{ flex: 2, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 5 }}><Badge t={row.trackStatus} /><span style={{ fontSize: 17, fontWeight: 700, color: '#1a1a1a' }}>{row.ultimateParent}</span></div>
        <div style={{ fontSize: 12, color: '#999' }}>
          {row.channel + ' · ' + row.sitesAcquired + '/' + row.sitesTotal + ' Sites · AE ' + userDisplayName(byId, row.accountExecutiveId || '—') + ' · CSAM ' + userDisplayName(byId, row.csAccountManagerId || '—')}
        </div>
      </div>
      <div style={{ flex: 1.1 }}>
        <div className="ch-col-label">Signals</div>
        <div style={{ display: 'flex', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#333' }}><Dot t={row.sentimentStatus} size={9} />Sentiment</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#333' }}><Dot t={row.budgetStatus} size={9} />Budget</div>
        </div>
      </div>
      <div style={{ flex: 1.3 }}>
        <div className="ch-col-label">Products Installed</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {row.installedProducts.length
            ? row.installedProducts.map((p) => (
              <span key={p.key} style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 9px', borderRadius: 6, border: '1px solid #e0e0e0', background: '#fff', fontSize: 12, color: '#333', fontWeight: 500 }}>{p.name}</span>
            ))
            : <span style={{ fontSize: 12, color: '#999' }}>—</span>}
        </div>
      </div>
      <div style={{ flex: 1 }}>
        <div className="ch-col-label">Revenue YTD</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a' }}>{money(row.revenueYtd)}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666', marginTop: 2 }}><Dot t={budgetToneFromBacklogPct(budgetPct)} size={8} />{pctOf(row.revenueYtd, row.annualBudget)}% of budget</div>
      </div>
      <div style={{ flex: 0.7, textAlign: 'right' }}>
        <div className="ch-col-label">Open Actions</div>
        <div style={{ fontSize: 19, fontWeight: 700, color: '#1a1a1a' }}>{row.openActions}</div>
        <div style={{ fontSize: 12, marginTop: 2 }}>{row.breachedActions > 0 ? <span style={{ color: '#b01400', fontWeight: 600 }}>{row.breachedActions} breached</span> : <span style={{ color: '#999' }}>On track</span>}</div>
      </div>
      <div style={{ color: '#ccc', fontSize: 22, flex: 'none' }}>›</div>
    </div>
  );
}

const PAGE_SIZES = ['10', '25', '50', '100'];

/** Sort + page-size selectors and prev/next controls for the portfolio list. */
export function PortfolioPagination({ offset, pageSize, totalCount, sortBy, onSort, onPageSize, onOffset }: {
  offset: number;
  pageSize: number;
  totalCount: number;
  sortBy: PortfolioSort;
  onSort: (v: PortfolioSort) => void;
  onPageSize: (v: number) => void;
  onOffset: (v: number) => void;
}) {
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.floor(offset / pageSize);
  const hasPrev = offset > 0;
  const hasNext = offset + pageSize < totalCount;
  const from = totalCount === 0 ? 0 : offset + 1;
  const to = Math.min(offset + pageSize, totalCount);

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', margin: '14px 0 6px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 12, color: '#999' }}>Sort</span>
        <CustomDropdown value={sortBy} options={[...PORTFOLIO_SORT_OPTIONS]} onChange={(v) => onSort(v as PortfolioSort)} />
        <span style={{ fontSize: 12, color: '#999', marginLeft: 8 }}>Show</span>
        <CustomDropdown value={String(pageSize)} options={PAGE_SIZES} onChange={(v) => onPageSize(Number(v))} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 12, color: '#666' }}>{from}–{to} of {totalCount}</span>
        <button onClick={() => hasPrev && onOffset(Math.max(0, offset - pageSize))} disabled={!hasPrev}
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', color: hasPrev ? '#333' : '#ccc', cursor: hasPrev ? 'pointer' : 'not-allowed' }}><HiChevronLeft /></button>
        <span style={{ fontSize: 12, color: '#666' }}>Page {currentPage + 1} of {pageCount}</span>
        <button onClick={() => hasNext && onOffset(offset + pageSize)} disabled={!hasNext}
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', color: hasNext ? '#333' : '#ccc', cursor: hasNext ? 'pointer' : 'not-allowed' }}><HiChevronRight /></button>
      </div>
    </div>
  );
}
